import React, { useEffect, useRef, useState } from 'react'
import {
  CCol, CRow, CForm, CFormInput, CFormLabel, CFormSelect, CButton, CAlert,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
} from '@coreui/react'
import { api, currentUser } from '../api/client'
import { hoyLocal } from '../utils/fecha'
import { aNumero, normalizarNumeroTexto } from '../utils/numeros'
import CIcon from '@coreui/icons-react'
import { cilUser } from '@coreui/icons'
import { CCard, CCardHeader, CCardBody } from '@coreui/react'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'

const columnas: WwColumn[] = [
  { key: 'socioCodigo', title: 'Codigo', type: 'number' },
  { key: 'socioFechaInicio', title: 'Fecha Inicio', type: 'date' },
  { key: 'socioFechaSalida', title: 'Fecha Salida', type: 'date' },
  { key: 'socioNombre', title: 'Nombre' },
  { key: 'socioEstado', title: 'Estado' },
  { key: 'socioProcentaje', title: 'Procentaje', type: 'number' },
  { key: 'socioTotalInversion', title: 'Total Inversion', type: 'currency' },
  { key: 'socioTotalPagos', title: 'Total Pagos', type: 'currency' },
  { key: 'empresaCodigo', title: 'Empresa Codigo' },
  { key: 'empresaNombre', title: 'Empresa Nombre' },
]

// Socios — réplica de sociogeneral.cs:55 (sociogeneral GX).
const SociosPage: React.FC = () => {
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [rows, setRows] = useState<any[]>([])
  const formRef = useRef<HTMLFormElement>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState('')
  const [abierto, setAbierto] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [editando, setEditando] = useState<any | null>(null)
  const [form, setForm] = useState({
    socioCodigo: '', socioNombre: '', socioEstado: 'Activo', socioProcentaje: '0',
    socioFechaInicio: hoyLocal(), socioFechaSalida: '',
  })

  const cargar = () => {
    setCargando(true)
    api.get<any[]>(`/socios?empresa=${empresa}`)
      .then(setRows)
      .catch((e: any) => setError(e.message))
      .finally(() => setCargando(false))
  }
  useEffect(cargar, [empresa])

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setGuardando(true)
    try {
      const body = {
        empresaCodigo: empresa,
        socioNombre: form.socioNombre,
        socioEstado: form.socioEstado,
        socioProcentaje: aNumero(form.socioProcentaje) || 0,
        socioFechaInicio: form.socioFechaInicio || undefined,
        socioFechaSalida: form.socioFechaSalida || undefined,
      }
      if (editando) {
        // socioww.cs:2449-2457 — DO UPDATE: webobject socio Mode="UPD" con
        // SocioCodigo y EmpresaCodigo (la PK viaja en la URL, no cambia).
        await api.put(`/socios/${editando.socioCodigo}`, body)
      } else {
        await api.post('/socios', {
          ...body,
          socioCodigo: Number(form.socioCodigo),
        })
      }
      setAbierto(false)
      setEditando(null)
      cargar()
    } catch (err: any) {
      setError(err.message)
    } finally {
      setGuardando(false)
    }
  }

  // socioww.cs:2449-2457 — Modificar: carga la fila en el form y abre el modal.
  const onEditar = (row: any) => {
    setError('')
    setEditando(row)
    setForm({
      socioCodigo: String(row.socioCodigo ?? ''),
      socioNombre: row.socioNombre ?? '',
      socioEstado: row.socioEstado ?? 'Activo',
      socioProcentaje: String(row.socioProcentaje ?? 0),
      socioFechaInicio: row.socioFechaInicio
        ? String(row.socioFechaInicio).slice(0, 10)
        : hoyLocal(),
      // socioFechaSalida sin fecha se guarda como 0001-01-01 en el backend
      socioFechaSalida: row.socioFechaSalida && !String(row.socioFechaSalida).startsWith('0001')
        ? String(row.socioFechaSalida).slice(0, 10)
        : '',
    })
    setAbierto(true)
  }

  const eliminar = (row: any) => {
    // socioww.cs:2459-2466 — DO DELETE: webobject socio Mode="DLT" con SocioCodigo.
    if (!window.confirm(`¿Eliminar el socio ${row.socioCodigo}?`)) return
    api.delete(`/socios/${row.socioCodigo}?empresa=${empresa}`)
      .then(() => cargar())
      .catch((e: any) => setError(e.message))
  }

  const [totalReg, setTotalReg] = useState(rows.length)
  return (
    <>
      <CRow className="mb-4">
        <CCol>
          <h4>Socio</h4>
          <small className="text-body-secondary">{totalReg} {totalReg === 1 ? 'registro' : 'registros'}</small>
        </CCol>
      </CRow>
      {error && <CAlert color="danger">{error}</CAlert>}

      <WwGrid
        title="Socio"
        columns={columnas}
        rows={rows}
        onTotalChange={setTotalReg}
        loading={cargando}
        busquedaPlaceholder="Buscar por socio o cédula..." 
        onAgregar={() => { setEditando(null); setAbierto(true) }}
        onEditar={onEditar}
        onEliminar={eliminar}
      />

      <CModal visible={abierto} onClose={() => { setAbierto(false); setEditando(null) }}>
        <CModalHeader>
          <CModalTitle>{editando ? 'Modificar socio' : 'Nuevo socio'}</CModalTitle>
        </CModalHeader>
        <CModalBody style={{ maxHeight: '75vh', overflowY: 'auto' }}>
          {error && <CAlert color="danger">{error}</CAlert>}
          <CForm ref={formRef} onSubmit={guardar}>
            {/* Tarjeta única (patrón formularios nuevos) — decisión Rafael */}
            <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
              <CCardHeader className="d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
                <CIcon icon={cilUser} size="sm" /> Datos del Socio
              </CCardHeader>
              <CCardBody style={{ padding: '14px 16px' }}>
                <CRow className="g-3">
                  <CCol md={6}><CFormLabel>Código</CFormLabel><CFormInput type="number" value={form.socioCodigo} disabled={editando !== null} onChange={(e) => setForm({ ...form, socioCodigo: e.target.value })} /></CCol>
                  {/* Mayúsculas (patrón GX: nombres en BD en mayúsculas) */}
                  <CCol md={6}><CFormLabel>Nombre</CFormLabel><CFormInput value={form.socioNombre} onChange={(e) => setForm({ ...form, socioNombre: e.target.value.toUpperCase() })} /></CCol>
                  <CCol md={6}>
                    <CFormLabel>Estado</CFormLabel>
                    <CFormSelect value={form.socioEstado} onChange={(e) => setForm({ ...form, socioEstado: e.target.value })}>
                      <option value="Activo">Activo</option>
                      <option value="Inactivo">Inactivo</option>
                    </CFormSelect>
                  </CCol>
                  <CCol md={6}><CFormLabel>Procentaje</CFormLabel><CFormInput type="number" value={form.socioProcentaje} onChange={(e) => setForm({ ...form, socioProcentaje: e.target.value })} onBlur={(e) => setForm({ ...form, socioProcentaje: normalizarNumeroTexto(e.target.value) })} /></CCol>
                  <CCol md={6}><CFormLabel>Fecha Inicio</CFormLabel><CFormInput type="date" value={form.socioFechaInicio} onChange={(e) => setForm({ ...form, socioFechaInicio: e.target.value })} /></CCol>
                  <CCol md={6}><CFormLabel>Fecha Salida</CFormLabel><CFormInput type="date" value={form.socioFechaSalida} onChange={(e) => setForm({ ...form, socioFechaSalida: e.target.value })} /></CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CForm>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => { setAbierto(false); setEditando(null) }}>Cancelar</CButton>
          <CButton color="primary" type="button" disabled={guardando} onClick={() => formRef.current?.requestSubmit()}>{guardando ? 'Guardando…' : editando ? 'Guardar cambios' : 'Crear socio'}</CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default SociosPage
